import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Check,
  ChevronLeft,
  FileText,
  Hourglass,
  Inbox,
  ListChecks,
  MoveRight,
  Pencil,
  Pin,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';

import { DirectionalIcon } from '../components/DirectionalIcon';
import { LtrText, UserText } from '../components/UserText';
import { PALETTES, readPalette, setPalette, type PaletteId } from '../lib/palette';

/**
 * بند R-20: هذه الصفحة **سقالة تطوير** — ملعب ثيم/بالتة وتعذيب bidi. وكانت تُشحن في
 * الإنتاج: مسارٌ بالغ في شجرة المالك وchunk كامل في `dist/`.
 *
 * والحل إبطالها بالبناء لا حذفها: `import.meta.env.DEV` ثابتٌ يُستبدَل وقت البناء،
 * فتصير الشرطية `false` في الإنتاج ويسقط المكوّن كله من الشجرة مع كل ما يستورده
 * (tree-shaking) — بينما يبقى الملف حاضرًا في التطوير حيث نفعه.
 *
 * ولم أحذفها لأنها تُلتقط لها صورة عند كل عمل على الاتجاه والثيم؛ ولم أستعمل
 * `routeFileIgnorePattern` لأن `routeTree.gen.ts` **متعقَّب في git**، فاستثناءٌ مشروط
 * بالإنتاج يجعل كل بناء يُوسِّخ الشجرة بملفٍّ مولَّد يتقلّب مع آخر أمر شُغِّل.
 */
export const Route = createFileRoute('/dev/directions')({
  component: import.meta.env.DEV ? PalettePlaygroundPage : DevOnlyNotice,
});

/* ────────────────────────────────────────────────────────────────────────────
 * ملعب البالتات الحيّ — يستبدل بوابة «الاتجاه أ / ب» (D72 ينقّح D48).
 *
 * لم تبقَ مقارنةُ اتجاهين: الاتجاه محسوم (حِبر وكهرمان)، والمتغيّر صار **قيمة
 * واحدة**: البالتة. فالصفحة تعرض الشاشتين نفسيهما — «اليوم» و«تفاصيل مشروع» —
 * وتبدّل بالتة الجذر حيًّا فوقهما بلا reload وبلا وميض. يقيسها المالك على
 * iPhone بحجمها الحقيقي ثم يعتمد بلمسة، والاختيار يُحفظ فيصبح هوية التطبيق.
 *
 * الفرق الجوهري عن النسخة السابقة: الصفحة **تستهلك رموز التصميم الحقيقية**
 * (`styles/tokens.css` + `styles/palettes.css`) ولا تعرّف طبقة رموز موازية ولا
 * hex واحدًا. ما يراه المالك هنا هو ما ستعرضه الشاشات الحقيقية حرفيًا — النسخة
 * السابقة كانت تُقاس على رموز مستنسخة يمكن أن تنحرف عن الأصل بلا أن يلاحظ أحد.
 *
 * والمحتوى يبقى وهميًا وثابتًا (عربي/إنجليزي مختلط حقيقي): الصفحة تفتح بلا خادم
 * وبلا جلسة، والحكم على اللون والتراتب لا على البيانات.
 * ──────────────────────────────────────────────────────────────────────── */

/* ═══ 1. المحتوى الوهمي ═══ */

interface Venture {
  readonly name: string;
  /** رمز من لوحة الـ 12 (§5.3) — لا hex في المكوّنات. */
  readonly color: string;
}

const V_LOQAL: Venture = { name: 'Loqal', color: 'var(--color-venture-indigo)' };
const V_NAWEL: Venture = { name: 'نَوَل ستوديو', color: 'var(--color-venture-rose)' };
const V_SHELVES: Venture = { name: 'متجر الرفوف', color: 'var(--color-venture-olive)' };

const GREETING = 'مساء النور، صالح';
const TODAY_LINE = 'الخميس 30 يوليو 2026';

interface AttentionItem {
  readonly key: string;
  readonly why: string;
  readonly subject: string;
  readonly venture: Venture;
  readonly action: string;
}

/* النبرة (§5.10): تصف الواقع بلا لوم وبلا علامات تعجب. */
const ATTENTION: readonly AttentionItem[] = [
  {
    key: 'STALLED',
    why: 'لم يُسجل نشاط منذ 14 يومًا',
    subject: 'Loqal — لوحة التحكم',
    venture: V_LOQAL,
    action: 'حدّد خطوة',
  },
  {
    key: 'TASK_OVERDUE',
    why: 'موعدها كان أمس',
    subject: 'مراجعة عقد Nawel Studio',
    venture: V_NAWEL,
    action: 'غفوة',
  },
];

interface TaskItem {
  readonly title: string;
  readonly venture: Venture;
  readonly meta: string;
  readonly overdue?: boolean;
  readonly done?: boolean;
}

const TODAY_TASKS: readonly TaskItem[] = [
  {
    title: 'اتصال بمورّد الرفوف — تأكيد كمية الطلب',
    venture: V_SHELVES,
    meta: 'موعدها كان أمس',
    overdue: true,
  },
  { title: 'مراجعة صفحة التسعير في Loqal قبل النشر', venture: V_LOQAL, meta: 'اليوم 4:00 م' },
  { title: 'Follow up with Fahad عن عرض السعر', venture: V_NAWEL, meta: 'اليوم' },
  { title: 'إرسال فاتورة Nawel Studio', venture: V_NAWEL, meta: 'أُنجزت قبل ساعة', done: true },
];

interface WaitingItem {
  readonly on: string;
  readonly task: string;
  readonly age: string;
  readonly aging?: boolean;
}

const WAITING: readonly WaitingItem[] = [
  { on: 'رد المحاسب على قائمة المصاريف', task: 'إقفال حسابات يونيو', age: 'منذ 5 أيام' },
  {
    on: 'Supplier quote — Al-Rashed Metals',
    task: 'تجديد رفوف الفرع الثاني',
    age: 'منذ 9 أيام',
    aging: true,
  },
];

interface EntryItemData {
  readonly kind: 'agent_log' | 'note' | 'status_update';
  readonly actor: string;
  readonly body: string;
  readonly when: string;
}

const AGENT_ENTRY: EntryItemData = {
  kind: 'agent_log',
  actor: 'Hermes',
  body: 'حدّثت أسعار المنافسين في Loqal: Notion وAirtable رفعا خطة الفريق — الفرق 12% عن تسعيرتنا.',
  when: 'قبل ساعتين',
};

const PROJECT_ENTRIES_TODAY: readonly EntryItemData[] = [
  AGENT_ENTRY,
  {
    kind: 'note',
    actor: 'أنت',
    body: 'تحديث Loqal: انتهيت من صفحة التسعير — باقي مراجعة النصوص العربية.',
    when: 'قبل 4 ساعات',
  },
];

const PROJECT_ENTRIES_OLD: readonly EntryItemData[] = [
  {
    kind: 'status_update',
    actor: 'أنت',
    body: 'وقفنا على مراجعة النصوص العربية في صفحة التسعير.',
    when: 'قبل 14 يومًا',
  },
  {
    kind: 'note',
    actor: 'أنت',
    body: 'Meeting with supplier — يشحنون الأحد، والسعر 2,400 ر.س بعد الخصم.',
    when: 'قبل 14 يومًا',
  },
  {
    kind: 'note',
    actor: 'أنت',
    body: 'Fahad asked for a 2-week extension — وافقت مبدئيًا على شرط تثبيت السعر.',
    when: 'قبل 16 يومًا',
  },
];

/* ═══ 2. قطع الواجهة — كلها برموز التصميم، ولا hex ولا رمز موازٍ ═══ */

/** صيغة الـ callout الواحدة (§2.9): غسل 8% + حدّ 30% + خيط 3px من النغمة. */
function calloutStyle(tone: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in oklch, ${tone} var(--mix-wash), var(--color-surface))`,
    borderColor: `color-mix(in oklch, ${tone} var(--mix-border), var(--color-border))`,
    borderInlineStartWidth: '3px',
    borderInlineStartColor: tone,
  };
}

function VentureBadge({ venture }: { venture: Venture }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-micro"
      style={{
        backgroundColor: `color-mix(in oklch, ${venture.color} 14%, var(--color-surface))`,
        color: venture.color,
      }}
    >
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full"
        style={{ backgroundColor: venture.color }}
      />
      <UserText>{venture.name}</UserText>
    </span>
  );
}

function SectionHead({
  icon,
  title,
  count,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
}) {
  return (
    <header className="mb-3 flex items-center gap-2">
      <span aria-hidden="true" className="flex text-text-muted">
        {icon}
      </span>
      <h3 className="text-heading">{title}</h3>
      {count !== undefined && (
        <LtrText className="rounded-sm bg-surface-raised px-1.5 py-0.5 text-micro text-text-secondary">
          {count}
        </LtrText>
      )}
    </header>
  );
}

function Card({ children, className }: { children: ReactNode; className?: string }) {
  const base = 'rounded-lg border border-border bg-surface';
  return <div className={className === undefined ? base : `${base} ${className}`}>{children}</div>;
}

function Hairline() {
  return <div aria-hidden="true" className="h-px bg-border" />;
}

function GhostButton({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-h-9 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-caption text-text-secondary">
      {children}
    </span>
  );
}

function PrimaryButton({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-h-9 shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 text-caption font-semibold text-on-accent">
      {children}
    </span>
  );
}

function ActorChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-sm bg-agent-subtle px-1.5 py-0.5 text-micro text-agent">
      <Sparkles aria-hidden="true" className="size-3" />
      <UserText>{name}</UserText>
    </span>
  );
}

function TaskRow({ task }: { task: TaskItem }) {
  const done = task.done === true;
  return (
    <div className="flex min-h-11 items-start gap-3 py-2.5">
      <span
        aria-hidden="true"
        className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border-[1.5px] ${
          done ? 'border-accent bg-accent text-on-accent' : 'border-border-strong'
        }`}
      >
        {done && <Check aria-hidden="true" className="size-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <UserText block className={`text-body ${done ? 'text-text-muted line-through' : ''}`}>
          {task.title}
        </UserText>
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <VentureBadge venture={task.venture} />
          <span
            className={`text-caption ${task.overdue === true ? 'text-warning' : 'text-text-muted'}`}
          >
            {task.meta}
          </span>
        </span>
      </span>
    </div>
  );
}

/** لغة الأثر (§5.7): خيط جانبي 2px بنفسجي للوكيل ورمادي للمالك. */
function EntryRow({ entry }: { entry: EntryItemData }) {
  const isAgent = entry.kind === 'agent_log';
  return (
    <div className={`border-s-2 ps-3 ${isAgent ? 'border-s-agent' : 'border-s-border'}`}>
      <div className="flex flex-wrap items-center gap-2">
        {isAgent ? (
          <ActorChip name={entry.actor} />
        ) : (
          <span className="text-caption font-medium text-text-secondary">
            {entry.kind === 'status_update' ? 'تحديث حالة' : 'ملاحظة'}
          </span>
        )}
        <span className="text-caption text-text-muted">{entry.when}</span>
        {isAgent && <span className="text-caption text-agent">لماذا؟</span>}
      </div>
      <UserText block className="mt-1 max-w-[65ch] text-body-lg">
        {entry.body}
      </UserText>
    </div>
  );
}

/* ═══ 3. الشاشة 1: اليوم ═══ */

function TodayScreen() {
  const open = TODAY_TASKS.filter((task) => task.done !== true).length;

  return (
    <div className="flex flex-col gap-8 p-4">
      <header>
        <h2 className="text-display">{GREETING}</h2>
        <p className="mt-1 text-caption text-text-secondary">{TODAY_LINE}</p>
        <div
          role="img"
          aria-label="أنجزت مهمة من أربع مهام اليوم"
          className="mt-3 h-[3px] overflow-hidden rounded-full bg-border"
        >
          <div className="h-full bg-accent" style={{ inlineSize: '25%' }} />
        </div>
      </header>

      {/* «يحتاج انتباهك» أول الأقسام دائمًا — لا يُدفن على الجوال (§6.1). */}
      <section>
        <SectionHead
          icon={<TriangleAlert aria-hidden="true" className="size-[18px]" />}
          title="يحتاج انتباهك"
          count={ATTENTION.length}
        />
        <div className="flex flex-col gap-2">
          {ATTENTION.map((item) => (
            <div
              key={item.key}
              className="flex items-start gap-3 rounded-lg border p-3"
              style={calloutStyle('var(--color-warning)')}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-body">{item.why}</span>
                <span className="mt-1 flex flex-wrap items-center gap-2">
                  <UserText className="text-caption font-medium">{item.subject}</UserText>
                  <VentureBadge venture={item.venture} />
                </span>
              </span>
              <GhostButton>{item.action}</GhostButton>
            </div>
          ))}
        </div>
      </section>

      {/* بطاقة الصندوق بين التحية والمهام على الجوال (§6.1). */}
      <Card className="flex items-center gap-3 p-4">
        <span aria-hidden="true" className="grid size-10 place-items-center rounded-md bg-field">
          <Inbox aria-hidden="true" className="text-accent" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-heading">
            الصندوق — <LtrText>4</LtrText> قيود غير مفروزة
          </span>
          <span className="mt-0.5 block text-caption text-text-secondary">
            آخرها: <UserText>Meeting with supplier — يشحنون الأحد</UserText>
          </span>
        </span>
        <DirectionalIcon
          icon={ChevronLeft}
          aria-hidden="true"
          className="shrink-0 text-text-muted"
        />
      </Card>

      <section>
        <SectionHead
          icon={<ListChecks aria-hidden="true" className="size-[18px]" />}
          title="مهام اليوم"
          count={open}
        />
        <Card className="px-4 py-1">
          {TODAY_TASKS.map((task, index) => (
            <div key={task.title}>
              {index > 0 && <Hairline />}
              <TaskRow task={task} />
            </div>
          ))}
        </Card>
      </section>

      {/* قيد كتبه وكيل — شارة الأثر ظاهرة بلا قراءة نص (§5.7). */}
      <section>
        <SectionHead
          icon={<Sparkles aria-hidden="true" className="size-[18px]" />}
          title="نشاط الوكلاء"
        />
        <Card className="p-4">
          <EntryRow entry={AGENT_ENTRY} />
          <p className="mt-3 text-caption text-text-muted">
            على <UserText>Loqal — Pricing page</UserText> · عبر MCP
          </p>
        </Card>
      </section>

      <section>
        <SectionHead
          icon={<Hourglass aria-hidden="true" className="size-[18px]" />}
          title="بانتظار"
          count={WAITING.length}
        />
        <Card className="px-4 py-1">
          {WAITING.map((item, index) => (
            <div key={item.on}>
              {index > 0 && <Hairline />}
              <div className="py-3">
                <span className="block text-heading">
                  بانتظار: <UserText>{item.on}</UserText>
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-2">
                  <UserText className="text-caption text-text-muted">{item.task}</UserText>
                  <span
                    className={`text-caption ${item.aging === true ? 'text-warning' : 'text-text-muted'}`}
                  >
                    {item.age}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </Card>
      </section>
    </div>
  );
}

/* ═══ 4. الشاشة 2: تفاصيل مشروع ═══ */

function ProjectScreen() {
  return (
    <div className="flex flex-col gap-8 p-4">
      <header
        className="border-s-[3px] ps-3"
        style={{ borderInlineStartColor: V_LOQAL.color }}
      >
        <div className="flex items-center gap-2">
          <DirectionalIcon
            icon={ChevronLeft}
            aria-hidden="true"
            className="size-4 text-text-muted"
          />
          <VentureBadge venture={V_LOQAL} />
        </div>
        <h2 className="mt-2 text-title">
          <UserText>Loqal — لوحة التحكم</UserText>
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusPill tone="var(--color-success)">نشط</StatusPill>
          <StatusPill tone="var(--color-text-secondary)">
            أولوية <LtrText>1</LtrText>
          </StatusPill>
          <StatusPill tone="var(--color-warning)">
            راكد <LtrText>14</LtrText> يومًا
          </StatusPill>
          <Pin aria-hidden="true" className="size-4 text-accent" />
        </div>
      </header>

      {/* «وين وصلنا؟» عرض حتمي محتسب من البيانات — بلا LLM (§6.3). */}
      <section>
        <SectionHead
          icon={<FileText aria-hidden="true" className="size-[18px]" />}
          title="وين وصلنا؟"
        />
        <Card className="p-4">
          <p className="text-caption text-text-secondary">
            آخر تحديث قبل <LtrText>14</LtrText> يومًا:
          </p>
          <UserText block className="mt-2 max-w-[65ch] text-body-lg">
            «وقفنا على مراجعة النصوص العربية في صفحة التسعير.»
          </UserText>
          <div className="my-3">
            <Hairline />
          </div>
          <p className="text-caption text-text-muted">
            <LtrText>3</LtrText> مهام مفتوحة · <LtrText>1</LtrText> بانتظار ·{' '}
            <LtrText>2</LtrText> قرارات
          </p>
        </Card>
      </section>

      {/* الخطوة التالية تُقرأ من العلامة الصريحة is_next_step — لا اشتقاق (D36). */}
      <section>
        <SectionHead
          icon={<MoveRight aria-hidden="true" className="size-[18px]" />}
          title="الخطوة التالية"
        />
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <DirectionalIcon icon={MoveRight} aria-hidden="true" className="mt-1 text-accent" />
            <span className="min-w-0 flex-1">
              <UserText block className="text-heading">
                مراجعة صفحة التسعير في Loqal قبل النشر
              </UserText>
              <span className="text-caption text-text-muted">معيّنة صريحًا · اليوم 4:00 م</span>
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <PrimaryButton>أنجزتها</PrimaryButton>
            <GhostButton>غيّر الخطوة</GhostButton>
          </div>
        </Card>
      </section>

      {/* اليومية هي الرئيسي: composer دائم أعلاها ثم القيود مجمعة بالأيام (§6.3). */}
      <section>
        {/* الحقل سطح غائر (D78): bg-field أغمق من البطاقة الحاضنة. */}
        <div
          aria-hidden="true"
          className="mb-4 flex items-center gap-2 rounded-md border border-border bg-field p-3"
        >
          <Pencil aria-hidden="true" className="size-4 text-text-muted" />
          <span className="flex-1 text-body text-text-muted">اكتب قيدًا…</span>
          <span className="flex overflow-hidden rounded-md border border-border text-micro">
            <span className="bg-accent-subtle px-2 py-1 text-text">ملاحظة</span>
            <span className="px-2 py-1 text-text-muted">تحديث</span>
          </span>
        </div>

        <DayDivider label="اليوم" />
        <div className="mt-3 flex flex-col gap-4">
          {PROJECT_ENTRIES_TODAY.map((entry) => (
            <EntryRow key={entry.body} entry={entry} />
          ))}
        </div>

        <div className="mt-6">
          <DayDivider
            label={
              <>
                قبل <LtrText>14</LtrText> يومًا
              </>
            }
          />
        </div>
        <div className="mt-3 flex flex-col gap-4">
          {PROJECT_ENTRIES_OLD.map((entry) => (
            <EntryRow key={entry.body} entry={entry} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** شارة حالة (§3.9): نص النغمة على غسل 8% منها، ووزن 500 لأن النسبة أقل من النص. */
function StatusPill({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-micro font-medium"
      style={{
        backgroundColor: `color-mix(in oklch, ${tone} var(--mix-wash), var(--color-surface))`,
        color: tone,
      }}
    >
      {children}
    </span>
  );
}

function DayDivider({ label }: { label: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-caption font-medium text-text-secondary">{label}</span>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  );
}

/* ═══ 5. المبدّل: بالتة على الجذر مباشرة (الوضع داكنٌ دائمًا — 2026-08-11) ═══ */

/**
 * الشريط لاصق في **مدى الإبهام**، ومرفوع فوق شريط التنقّل السفلي للقشرة (56px +
 * منطقة الأمان) كي لا يختفي تحته على iPhone؛ وعلى الديسكتوب لا شريط سفلي فيهبط
 * إلى الحافة. وz من `--z-sticky` لا برقم مرتجل (D80).
 */
function SwitcherBar() {
  const [palette, setCurrentPalette] = useState<PaletteId>(() => readPalette());

  return (
    <div
      className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-[var(--z-sticky)] -mx-4 mt-8 border-t border-border bg-bg px-4 pt-3 lg:bottom-0 lg:-mx-8 lg:px-8"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex w-full max-w-[430px] flex-col gap-3 lg:max-w-none">
        <div role="group" aria-label="البالتة" className="grid grid-cols-6 gap-2">
          {PALETTES.map((meta) => {
            const selected = meta.id === palette;
            return (
              <button
                key={meta.id}
                type="button"
                aria-pressed={selected}
                aria-label={`بالتة ${meta.label}`}
                title={meta.gist}
                onClick={() => {
                  setPalette(meta.id);
                  setCurrentPalette(meta.id);
                }}
                className={`flex min-h-11 flex-col items-center gap-1 rounded-md border p-1.5 transition-colors duration-[var(--dur-1)] ${
                  selected ? 'border-accent bg-accent-subtle' : 'border-border'
                }`}
              >
                {/* الشريحة تحمل data-palette فتعرض هويتها الحقيقية لا تقريبًا مكتوبًا. */}
                <span
                  data-palette={meta.id}
                  aria-hidden="true"
                  className="grid size-8 place-items-center rounded-sm border border-border bg-bg"
                >
                  <span className="size-3.5 rounded-full bg-accent" />
                </span>
                <span
                  className={`text-micro leading-none ${selected ? 'text-accent' : 'text-text-muted'}`}
                >
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>


        <p aria-live="polite" className="text-micro text-text-muted">
          البالتة تُحفظ على هذا الجهاز فورًا — لا حفظ على الخادم. والوضع داكنٌ
          دائمًا (حُذف الفاتح بأمر المالك 2026-08-11).
        </p>
      </div>
    </div>
  );
}

/* ═══ 6. الصفحة ═══ */

function ScreenFrame({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-micro text-text-muted">{label}</p>
      {/* إطار بعرض iPhone: الحكم يكون على الحجم الحقيقي لا على شاشة عريضة. */}
      <div className="mx-auto w-full max-w-[430px] overflow-hidden rounded-lg border border-border">
        {children}
      </div>
    </div>
  );
}

function PalettePlaygroundPage() {
  return (
    <div className="flex flex-col">
      <header className="mb-8">
        <p className="text-micro text-text-muted">ملعب البالتات · D72 (يستبدل بوابة D48)</p>
        <h1 className="mt-1 text-display">تختار البالتة، لا التفاصيل</h1>
        <div className="mt-3 flex max-w-[65ch] flex-col gap-3 text-body text-text-secondary">
          <p>
            الشاشتان أدناه — «اليوم» و«تفاصيل مشروع» — بنفس المحتوى العربي/الإنجليزي المختلط ونفس
            ترتيب المعلومات، ومرسومتان برموز التصميم الحقيقية. بدّل البالتة من الشريط السفلي فيتغيّر
            كل شيء فورًا بلا إعادة تحميل.
          </p>
          <p>
            الست كلها مقيسة على عتبات التباين (نص ≥7:1 · ثانوي ومكتوم ≥4.5:1)، والافتراضي «كهرمان»
            وهو الوحيد الذي له وضع فاتح محلول. اقرأها على جوالك بحجمها الحقيقي، ثم اتركها على ما
            تحب — اختيارك هو الإعداد نفسه.
          </p>
        </div>
      </header>

      <div className="grid gap-10 lg:grid-cols-2">
        <ScreenFrame label="شاشة 1 — اليوم">
          <TodayScreen />
        </ScreenFrame>
        <ScreenFrame label="شاشة 2 — تفاصيل مشروع">
          <ProjectScreen />
        </ScreenFrame>
      </div>

      <SwitcherBar />
    </div>
  );
}

/** بديل الإنتاج: نصٌّ ساكن بلا تبعيات — وجوده وحده يكفي ليُشذَّب الملعب كله. */
function DevOnlyNotice() {
  return <p className="p-6 text-body text-text-muted">صفحة تطوير — غير متاحة في النسخة المنشورة.</p>;
}
